import React, { createContext, useContext, useState, useEffect } from 'react';
import { setAnalyticsUser } from '../../../services/analytics';
import { 
  auth, 
  isFirebaseConfigured,
  db,
  ref,
  onValue,
  set
} from '../../../firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  User,
  UserCredential
} from 'firebase/auth';
import { isTauri } from '@shared/utils/environment';
import { remoteConfigService } from '../../../services/remoteConfigService';

export interface MockUser {
  uid: string;
  email: string;
  displayName?: string;
  password?: string;
}

export type CurrentUser = User | MockUser | null;

export interface AuthContextType {
  currentUser: CurrentUser;
  isDemoMode: boolean;
  subscriptionStatus: 'active' | 'inactive';
  updateSubscriptionStatus: (status: 'active' | 'inactive') => Promise<void>;
  signup: (email: string, password: string) => Promise<UserCredential | { user: MockUser }>;
  login: (email: string, password: string) => Promise<UserCredential | { user: MockUser }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  loginWithGoogle: () => Promise<UserCredential | { user: MockUser }>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

// Funkcje pomocnicze dla trybu Mock (LocalStorage)
const getMockUsers = (): Record<string, MockUser> => {
  const users = localStorage.getItem('mock_users');
  if (!users) {
    const defaultUsers: Record<string, MockUser> = {
      'demo@example.com': {
        uid: 'mock_uid_demo',
        email: 'demo@example.com',
        password: 'password123'
      }
    };
    localStorage.setItem('mock_users', JSON.stringify(defaultUsers));
    return defaultUsers;
  }
  return JSON.parse(users);
};

const setMockUsers = (users: Record<string, MockUser>) => {
  localStorage.setItem('mock_users', JSON.stringify(users));
};

const getMockSession = (): MockUser | null => {
  const session = localStorage.getItem('mock_user_session');
  return session ? JSON.parse(session) : null;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<CurrentUser>(() => {
    if (isFirebaseConfigured) return null;
    return getMockSession();
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState<'active' | 'inactive'>('inactive');
  const [loading, setLoading] = useState(() => !!isFirebaseConfigured);

  // Wczytywanie subskrypcji w trybie demo przy montowaniu
  useEffect(() => {
    if (!isFirebaseConfigured) {
      const stored = localStorage.getItem('mock_subscription_status') as 'active' | 'inactive' | null;
      setSubscriptionStatus(stored || 'inactive');
    }
  }, []);

  // Rejestracja
  function signup(email: string, password: string): Promise<UserCredential | { user: MockUser }> {
    if (isFirebaseConfigured && auth) {
      return createUserWithEmailAndPassword(auth, email, password);
    } else {
      return new Promise<{ user: MockUser }>((resolve, reject) => {
        setTimeout(() => {
          const users = getMockUsers();
          if (users[email]) {
            reject(new Error('Użytkownik o tym adresie e-mail już istnieje.'));
            return;
          }
          const uid = 'mock_uid_' + Math.random().toString(36).substring(2, 11);
          users[email] = { uid, email, password };
          setMockUsers(users);
          
          const sessionUser: MockUser = { uid, email };
          localStorage.setItem('mock_user_session', JSON.stringify(sessionUser));
          setCurrentUser(sessionUser);
          resolve({ user: sessionUser });
        }, 800);
      });
    }
  }

  // Logowanie
  function login(email: string, password: string): Promise<UserCredential | { user: MockUser }> {
    if (isFirebaseConfigured && auth) {
      return signInWithEmailAndPassword(auth, email, password);
    } else {
      return new Promise<{ user: MockUser }>((resolve, reject) => {
        setTimeout(() => {
          const users = getMockUsers();
          const user = users[email];
          if (!user || user.password !== password) {
            reject(new Error('Nieprawidłowy e-mail lub hasło.'));
            return;
          }
          const sessionUser: MockUser = { uid: user.uid, email: user.email };
          localStorage.setItem('mock_user_session', JSON.stringify(sessionUser));
          setCurrentUser(sessionUser);
          resolve({ user: sessionUser });
        }, 800);
      });
    }
  }

  // Logowanie przez Google
  function loginWithGoogle(): Promise<UserCredential | { user: MockUser }> {
    if (isTauri()) {
      if (isFirebaseConfigured && auth) {
        return new Promise<UserCredential | { user: MockUser }>((resolve, reject) => {
          import('@tauri-apps/plugin-shell').then(({ open }) => {
            const configuredUrl = remoteConfigService.loginUrl;
            const fallbackUrl = import.meta.env.DEV
              ? 'http://localhost:1425/auth-desktop'
              : `${window.location.origin}/auth-desktop`;
            const desktopAuthUrl = (configuredUrl && configuredUrl.trim() !== '') ? configuredUrl : fallbackUrl;
            open(desktopAuthUrl).then(() => {
              resolve({ user: { uid: 'pending_desktop_auth', email: 'desktop-auth-pending' } });
            }).catch(reject);
          }).catch(reject);
        });
      } else {
        return new Promise<{ user: MockUser }>((resolve) => {
          setTimeout(() => {
            const sessionUser: MockUser = { 
              uid: 'mock_uid_google', 
              email: 'google-user@gmail.com',
              displayName: 'Demo Google User (Desktop)'
            };
            localStorage.setItem('mock_user_session', JSON.stringify(sessionUser));
            setCurrentUser(sessionUser);
            resolve({ user: sessionUser });
          }, 800);
        });
      }
    }

    // Standardowe logowanie przeglądarkowe dla wersji Web
    if (isFirebaseConfigured && auth) {
      const provider = new GoogleAuthProvider();
      return signInWithPopup(auth, provider);
    } else {
      return new Promise<{ user: MockUser }>((resolve) => {
        setTimeout(() => {
          const sessionUser: MockUser = { 
            uid: 'mock_uid_google', 
            email: 'google-user@gmail.com',
            displayName: 'Demo Google User'
          };
          localStorage.setItem('mock_user_session', JSON.stringify(sessionUser));
          setCurrentUser(sessionUser);
          resolve({ user: sessionUser });
        }, 800);
      });
    }
  }

  // Wylogowanie
  function logout(): Promise<void> {
    if (isFirebaseConfigured && auth) {
      return signOut(auth);
    } else {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          localStorage.removeItem('mock_user_session');
          setCurrentUser(null);
          resolve();
        }, 500);
      });
    }
  }

  // Reset hasła
  function resetPassword(email: string): Promise<void> {
    if (isFirebaseConfigured && auth) {
      return sendPasswordResetEmail(auth, email);
    } else {
      return new Promise<void>((resolve, reject) => {
        setTimeout(() => {
          const users = getMockUsers();
          if (!users[email]) {
            reject(new Error('Nie znaleziono użytkownika o tym adresie e-mail.'));
            return;
          }
          resolve();
        }, 800);
      });
    }
  }

  // Zmiana statusu subskrypcji
  async function updateSubscriptionStatus(status: 'active' | 'inactive') {
    if (isFirebaseConfigured && db && currentUser) {
      const subRef = ref(db, `users/${currentUser.uid}/subscriptionStatus`);
      await set(subRef, status);
    } else {
      // Tryb Demo
      localStorage.setItem('mock_subscription_status', status);
      setSubscriptionStatus(status);
      // Wywołaj event odświeżenia
      window.dispatchEvent(new CustomEvent('mock_db_update', { detail: { uid: currentUser?.uid } }));
    }
  }

  // Synchronizacja właściwości z GA4
  useEffect(() => {
    if (currentUser) {
      setAnalyticsUser(currentUser.uid, { subscription_status: subscriptionStatus });
    } else {
      setAnalyticsUser(null);
    }
  }, [currentUser, subscriptionStatus]);

  // Obserwowanie stanu zalogowania oraz subskrypcji w Firebase
  useEffect(() => {
    if (isFirebaseConfigured && auth) {
      let dbUnsubscribe: (() => void) | undefined;

      const unsubscribe = onAuthStateChanged(auth, (user) => {
        setCurrentUser(user);
        setAnalyticsUser(user ? user.uid : null);
        
        if (user && db) {
          const subRef = ref(db, `users/${user.uid}/subscriptionStatus`);
          dbUnsubscribe = onValue(subRef, (snapshot) => {
            const val = snapshot.val();
            const newStatus = val === 'active' ? 'active' : 'inactive';
            setSubscriptionStatus(newStatus);
            setAnalyticsUser(user.uid, { subscription_status: newStatus });
            setLoading(false);
          }, (err) => {
            console.error("Błąd nasłuchiwania subskrypcji:", err);
            setSubscriptionStatus('inactive');
            setLoading(false);
          });
        } else {
          setSubscriptionStatus('inactive');
          setLoading(false);
        }
      });

      return () => {
        unsubscribe();
        if (dbUnsubscribe) dbUnsubscribe();
      };
    }
  }, []);

  // Nasłuchiwanie Deep Linków dla natywnego logowania Google w Tauri
  useEffect(() => {
    if (!isTauri() || !isFirebaseConfigured || !auth) return;

    let unlisten: (() => void) | undefined;

    const setupDeepLink = async () => {
      try {
        const { onOpenUrl } = await import('@tauri-apps/plugin-deep-link');

        unlisten = await onOpenUrl((urls: string[]) => {
          for (const urlStr of urls) {
            console.log("🔗 Otrzymano Deep Link w Tauri:", urlStr);
            if (urlStr.startsWith('practiceworks://')) {
              try {
                // Wykorzystujemy bezpieczny parser URL
                const url = new URL(urlStr);
                
                // Szukamy w query params lub w hashu jako fallback
                let idToken = url.searchParams.get('idToken');
                let accessToken = url.searchParams.get('accessToken');
                
                if (!idToken && url.hash) {
                  const hashParams = new URLSearchParams(url.hash.substring(1));
                  idToken = hashParams.get('idToken');
                  accessToken = hashParams.get('accessToken');
                }

                if (idToken && auth) {
                  const credential = GoogleAuthProvider.credential(idToken, accessToken || undefined);
                  signInWithCredential(auth, credential).then((result) => {
                    console.log("🔥 Zalogowano przez Deep Link w aplikacji natywnej:", result.user.email);
                  }).catch((err) => {
                    console.error("Błąd autoryzacji credential z Deep Link:", err);
                  });
                } else {
                  console.warn("Brak idToken w otrzymanym Deep Linku.");
                }
              } catch (urlErr) {
                console.error("Błąd podczas parsowania adresu Deep Link:", urlErr);
              }
            }
          }
        });
      } catch (err) {
        console.warn("Błąd konfiguracji nasłuchiwania Deep Link:", err);
      }
    };

    setupDeepLink();

    return () => {
      if (unlisten) unlisten();
    };
  }, []);

  const value: AuthContextType = {
    currentUser,
    isDemoMode: !isFirebaseConfigured,
    subscriptionStatus,
    updateSubscriptionStatus,
    signup,
    login,
    logout,
    resetPassword,
    loginWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
